// components/integrations/shared/ApiTester.tsx
import React, { useState, useEffect } from "react";
import { Send, RefreshCw, Copy, Check, ChevronDown } from "lucide-react";
import axios from "axios";
import { MAIN_API_URL } from "../../../config/api";
import { alertSuccess } from "../../../helpers/alertError";

export interface TesterEndpoint {
  key: string;
  label: string;
  method?: "POST" | "GET" | "PUT" | "DELETE";
  endpoint: string;
  body?: Record<string, any>;
}

interface ApiTesterProps {
  endpoints: TesterEndpoint[];
  apiKey?: string;
  companyId?: string;
  moduleName: string;
}

export const ApiTester: React.FC<ApiTesterProps> = ({ endpoints, apiKey, companyId, moduleName }) => {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusCode, setStatusCode] = useState<number | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string>(endpoints[0]?.key ?? "");

  const active = endpoints.find((e) => e.key === activeKey) ?? endpoints[0];

  const [formData, setFormData] = useState<Record<string, any>>({
    api_key: apiKey || "",
    ...(active?.body ?? {}),
  });

  // Réinitialise le body quand on change d'endpoint
  useEffect(() => {
    setFormData({
      api_key: apiKey || "",
      ...(active?.body ?? {}),
    });
    setResponse(null);
    setError(null);
    setStatusCode(null);
  }, [activeKey]);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!active) return;

    setLoading(true);
    setError(null);
    setResponse(null);
    setStatusCode(null);

    const method = active.method ?? "POST";
    const body = {
      entreprise_id: companyId,
      ...formData,
    };
    delete (body as any).api_key;

    try {
      const url = `${MAIN_API_URL}${active.endpoint}`;
      const res = await axios({
        method,
        url,
        data: method === "GET" ? undefined : body,
        params: method === "GET" ? body : undefined,
        headers: {
          "X-API-Key": formData.api_key,
          "Content-Type": "application/json",
        },
      });

      setStatusCode(res.status);
      setResponse(res.data);
      alertSuccess("Test réussi !");
    } catch (err: any) {
      setStatusCode(err.response?.status || 500);
      if (err.response?.data) {
        setResponse(err.response.data);
        setError(err.response.data.message || "Erreur lors du test");
      } else {
        setError(err.message || "Erreur de connexion");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderFields = () => {
    const fields = [];
    const skipFields = ["api_key", "entreprise_id"];

    for (const [key, value] of Object.entries(formData)) {
      if (skipFields.includes(key)) continue;

      if (typeof value === "string" && key.includes("description")) {
        fields.push(
          <div key={key} className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} *
            </label>
            <textarea
              name={key}
              value={value}
              onChange={handleChange}
              required
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>
        );
      } else if (typeof value === "string") {
        fields.push(
          <div key={key} className="md:col-span-1">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} *
            </label>
            <input
              type={key.includes("email") ? "email" : key.includes("phone") || key === "to" ? "tel" : "text"}
              name={key}
              value={value}
              onChange={handleChange}
              required={!key.includes("phone") && key !== "from"}
              placeholder={
                key === "to" || key.includes("phone") ? "+229XXXXXXXX" : key === "message" ? "Votre message..." : ""
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        );
      }
    }

    return fields;
  };

  if (!active) return null;

  const method = active.method ?? "POST";

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="border-b border-slate-200 p-5 bg-gradient-to-r from-indigo-50 to-blue-50">
        <div className="flex items-center gap-3">
          <Send size={20} className="text-indigo-600" />
          <h3 className="font-semibold text-slate-800">Tester votre intégration</h3>
          <span className="ml-auto text-xs bg-indigo-100 text-indigo-700 px-2.5 py-1 rounded-full font-medium">
            En direct
          </span>
        </div>
      </div>

      <div className="p-5">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* ✅ Select endpoint */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Endpoint à tester</label>
            <div className="relative">
              <select
                value={activeKey}
                onChange={(e) => setActiveKey(e.target.value)}
                className="w-full appearance-none bg-white border border-slate-300 rounded-lg px-4 py-2.5 pr-10 text-sm font-medium text-slate-700 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors cursor-pointer"
              >
                {endpoints.map((ep) => (
                  <option key={ep.key} value={ep.key}>
                    {ep.method ?? "POST"} — {ep.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={16}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>
            <div className="mt-1 text-xs text-slate-500">
              <span className="font-medium text-slate-700 mr-2">{method}</span>
              <code className="bg-slate-100 px-2 py-0.5 rounded text-[11px]">
                {MAIN_API_URL}
                {active.endpoint}
              </code>
            </div>
          </div>

          {/* Clé API */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Clé API *</label>
            <input
              type="text"
              name="api_key"
              value={formData.api_key}
              onChange={handleChange}
              required
              placeholder="Collez votre clé API"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {apiKey && (
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, api_key: apiKey }))}
                className="mt-1 text-xs text-primary hover:text-primary/80"
              >
                Utiliser ma clé API {moduleName}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{renderFields()}</div>

          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
            {loading ? "Envoi..." : "Tester"}
          </button>
        </form>

        {/* Résultat */}
        {statusCode && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  statusCode >= 200 && statusCode < 300 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                }`}
              >
                Statut: {statusCode}
              </span>
              <span className="text-xs text-slate-500">
                {statusCode >= 200 && statusCode < 300 ? "✅ Succès" : "❌ Erreur"}
              </span>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {response && (
              <div className="bg-slate-900 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-800">
                  <span className="text-xs text-slate-400 font-mono">Réponse</span>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(response, null, 2), "response")}
                    className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs text-white flex items-center gap-1 transition-colors"
                  >
                    {copied === "response" ? <Check size={14} /> : <Copy size={14} />}
                    Copier
                  </button>
                </div>
                <pre className="p-4 overflow-x-auto max-h-60">
                  <code className="text-sm text-slate-100 font-mono whitespace-pre">
                    {JSON.stringify(response, null, 2)}
                  </code>
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
